// src/components/issues/tableau/PopoverFiltre.tsx — SCE Issues
// Le filtre d'une colonne : ses valeurs, cochables, avec le nombre de problèmes
// derrière chacune. Les nombres tiennent compte des AUTRES filtres (pas du sien,
// sinon cocher une valeur ferait tomber les autres à zéro) : on voit ce que
// donnerait chaque coche avant de la faire.

import { useMemo, useState } from 'react'
import { Popover } from '@/components/ui/Popover'
import type { Issue } from '@/types/issue.types'
import {
  DEFINITIONS_COLONNES,
  FILTRES_ETAT,
  compterValeurs,
  valeursPossibles,
  type ColonneId,
  type ContexteTableau,
  type ReglagesTableau,
} from '@/utils/tableauProblemes'
import { LibelleValeur, texteValeur } from './valeurs'

const SEUIL_RECHERCHE = 8

export interface PopoverFiltreProps {
  col: ColonneId
  ancre: HTMLElement
  issues: readonly Issue[]
  reglages: ReglagesTableau
  recherche: string
  ctx: ContexteTableau
  onBasculer: (col: ColonneId, valeur: string, actif: boolean) => void
  /** Efface le filtre des colonnes données. */
  onEffacer: (cols: readonly ColonneId[]) => void
  onClose: () => void
}

export function PopoverFiltre({
  col,
  ancre,
  issues,
  reglages,
  recherche,
  ctx,
  onBasculer,
  onEffacer,
  onClose,
}: PopoverFiltreProps): JSX.Element {
  // L'État n'a pas de valeurs à lui : il porte les filtres Statut et Criticité.
  const sections = col === 'etat' ? FILTRES_ETAT : [col]
  const libelle = DEFINITIONS_COLONNES[col].libelle
  const rien = sections.every((c) => (reglages.filtres[c]?.length ?? 0) === 0)

  return (
    <Popover ancre={ancre} onClose={onClose} titre={`Filtrer : ${libelle}`}>
      <div className="popover-entete">
        <span>{libelle}</span>
        <button type="button" onClick={() => onEffacer(sections)} disabled={rien}>
          Effacer
        </button>
      </div>
      <div className="popover-liste">
        {sections.map((c) => (
          <SectionFiltre
            key={c}
            col={c}
            titre={sections.length > 1 ? DEFINITIONS_COLONNES[c].libelle : null}
            // « À qualifier » en criticité redirait le statut « À qualifier ».
            exclure={col === 'etat' && c === 'criticite' ? ['À qualifier'] : []}
            issues={issues}
            reglages={reglages}
            recherche={recherche}
            ctx={ctx}
            onBasculer={(v, actif) => onBasculer(c, v, actif)}
          />
        ))}
      </div>
    </Popover>
  )
}

function SectionFiltre({
  col,
  titre,
  exclure,
  issues,
  reglages,
  recherche,
  ctx,
  onBasculer,
}: {
  col: ColonneId
  titre: string | null
  exclure: readonly string[]
  issues: readonly Issue[]
  reglages: ReglagesTableau
  recherche: string
  ctx: ContexteTableau
  onBasculer: (valeur: string, actif: boolean) => void
}): JSX.Element {
  const [saisie, setSaisie] = useState('')
  const toutes = useMemo(
    () => valeursPossibles(issues, col, ctx).filter((v) => !exclure.includes(v)),
    [issues, col, ctx, exclure],
  )
  const nombres = useMemo(
    () => compterValeurs(issues, reglages, recherche, ctx, col),
    [issues, reglages, recherche, ctx, col],
  )
  const choisies = reglages.filtres[col] ?? []
  const t = saisie.trim().toLowerCase()
  const visibles = t ? toutes.filter((v) => texteValeur(col, v).toLowerCase().includes(t)) : toutes
  const libelle = DEFINITIONS_COLONNES[col].libelle

  return (
    <div className="popover-section">
      {titre && <div className="popover-section-titre">{titre}</div>}
      {toutes.length > SEUIL_RECHERCHE && (
        <input
          type="search"
          className="popover-recherche"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          placeholder="Chercher…"
          aria-label={`Chercher dans ${libelle}`}
          autoFocus
        />
      )}
      {visibles.length === 0 && <p className="popover-vide">Aucune valeur</p>}
      {visibles.map((v) => {
        const n = nombres.get(v) ?? 0
        return (
          <label key={v} className={n === 0 ? 'tableau-option tableau-option--vide' : 'tableau-option'}>
            <input type="checkbox" checked={choisies.includes(v)} onChange={(e) => onBasculer(v, e.target.checked)} />
            <span className="tableau-option-libelle">
              <LibelleValeur col={col} valeur={v} />
            </span>
            <span className="tableau-option-nombre">{n}</span>
          </label>
        )
      })}
    </div>
  )
}
